import Enum from 'enum'
import colors from 'styles/colors.json'

import { ConversationStatusEnum } from 'constants/ConversationStatusConstants'
import { SortConversationsEnum } from 'constants/SortConversationsConstants'
import {
  CONVERSATION_TAG_KEY,
  STATUS_FILTER_KEY,
  PARTICIPANT_FILTER_KEY,
  ADVANCED_SEARCH_KEY,
  SORT_KEY,
} from 'constants/FilterConstants'

const FILTER_KEYS = [CONVERSATION_TAG_KEY, STATUS_FILTER_KEY, PARTICIPANT_FILTER_KEY]
const SORT_KEYS = [SORT_KEY]
const OPEN_AND_PENDING = [ConversationStatusEnum.OPEN.key, ConversationStatusEnum.PENDING.key]

const OPEN_VIEW_QUERY = {}
OPEN_VIEW_QUERY[STATUS_FILTER_KEY] = [ConversationStatusEnum.OPEN.key]

const PENDING_VIEW_QUERY = {}
PENDING_VIEW_QUERY[STATUS_FILTER_KEY] = [ConversationStatusEnum.PENDING.key]

const CLOSED_VIEW_QUERY = {}
CLOSED_VIEW_QUERY[STATUS_FILTER_KEY] = [ConversationStatusEnum.CLOSED.key]

const NO_PARTICIPANTS_QUERY = {}
NO_PARTICIPANTS_QUERY[PARTICIPANT_FILTER_KEY] = '0'
NO_PARTICIPANTS_QUERY[STATUS_FILTER_KEY] = OPEN_AND_PENDING

const ALL_QUERY = {}

// set any filter key that is unset to undefined
const makeQuery = function(query) {
  const newQuery = _.cloneDeep(query)

  _.each([...FILTER_KEYS, ...SORT_KEYS], function(key) {
    if ((newQuery[key] == null)) { newQuery[key] = undefined }
    return true
  })

  return newQuery
}

const makeParticipatingQuery = function(currentUserId) {
  const query = {}
  query[PARTICIPANT_FILTER_KEY] = [currentUserId.toString()]
  query[STATUS_FILTER_KEY] = OPEN_AND_PENDING
  return query
}

export const isSearchViewActive = query => query[STATUS_FILTER_KEY] || query[CONVERSATION_TAG_KEY] || query[PARTICIPANT_FILTER_KEY]

export const SearchView = {
  participating: {
    name: 'Your Conversations',
    color: colors['open-status-color'],
    isActive(query, currentUserId) {
      return _.isEqual(_.pick(query, FILTER_KEYS), makeParticipatingQuery(currentUserId))
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), makeParticipatingQuery(currentUserId)))
    }
  },
  noParticipants: {
    name: 'No Participants',
    color: colors['open-status-color'],
    isActive(query) {
      return _.isEqual(_.pick(query, FILTER_KEYS), NO_PARTICIPANTS_QUERY)
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), NO_PARTICIPANTS_QUERY))
    }
  },
  open: {
    name: 'Open',
    color: colors['open-status-color'],
    isActive(query) {
      return _.isEqual(_.pick(query, FILTER_KEYS), OPEN_VIEW_QUERY)
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), OPEN_VIEW_QUERY))
    }
  },
  pending: {
    name: 'Pending',
    color: colors['pending-status-color'],
    isActive(query) {
      return _.isEqual(_.pick(query, FILTER_KEYS), PENDING_VIEW_QUERY)
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), PENDING_VIEW_QUERY))
    }
  },
  closed: {
    name: 'Closed',
    color: colors['closed-status-color'],
    isActive(query) {
      return _.isEqual(_.pick(query, FILTER_KEYS), CLOSED_VIEW_QUERY)
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), CLOSED_VIEW_QUERY))
    }
  },
  all: {
    name: 'All',
    color: colors['color-navy-dark'],
    isActive(query) {
      return _.isEmpty(_.pick(query, FILTER_KEYS))
    },
    getQuery(currentUserId, currentQuery) {
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), ALL_QUERY))
    }
  }
}

export const SearchViewEnum = new Enum(SearchView)

export const getCurrentView = (query, currentUserId) => {
  const activeView = _.chain(SearchView)
    .map((value, key) => ({ value, key }))
    .filter(({ value }) => value.isActive(query, currentUserId))
    .map(({ key }) => key)
    .first()
    .value()

  return activeView || ADVANCED_SEARCH_KEY
}

const hasAdvancedConversationStatusFilters = (query) => {
  const activeSecondaryView = _.chain(SecondarySearchView)
    .map((value, key) => ({ value, key }))
    .filter(({ value }) => value.isActive(query))
    .map(({ key }) => key)
    .value()

  return _.isEmpty(activeSecondaryView)
}

export const PrimarySearchView = {
  participating: {
    name: 'My Conversations',
    isActive(query, currentUserId) {
      const filters = _.pick(query, FILTER_KEYS)
      const primaryFilters = filters[PARTICIPANT_FILTER_KEY]
      const customFilters = filters[CONVERSATION_TAG_KEY]

      return _.isEqual(primaryFilters, [currentUserId.toString()]) && _.isEmpty(customFilters) && !hasAdvancedConversationStatusFilters(query)
    },
    getQuery({ currentUserId, currentQuery, statusFilters }) {
      const query = {}
      query[PARTICIPANT_FILTER_KEY] = [currentUserId.toString()]
      query[STATUS_FILTER_KEY] = statusFilters
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), query))
    }
  },
  noParticipants: {
    name: 'Unassigned',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const primaryFilters = filters[PARTICIPANT_FILTER_KEY]
      const customFilters = filters[CONVERSATION_TAG_KEY]

      return (_.isEqual(primaryFilters, '0') || _.isEqual(primaryFilters, ['0'])) && _.isEmpty(customFilters) && !hasAdvancedConversationStatusFilters(query)
    },
    getQuery({ currentQuery, statusFilters }) {
      const query = {}
      query[PARTICIPANT_FILTER_KEY] = '0'
      query[STATUS_FILTER_KEY] = statusFilters
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), query))
    }
  },
  all: {
    name: 'All Conversations',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const primaryFilters = filters[PARTICIPANT_FILTER_KEY]
      const customFilters = filters[CONVERSATION_TAG_KEY]

      return _.isEmpty(primaryFilters) && _.isEmpty(customFilters) && !hasAdvancedConversationStatusFilters(query)
    },
    getQuery({ currentQuery, statusFilters }) {
      const query = {}
      query[STATUS_FILTER_KEY] = statusFilters
      return makeQuery(Object.assign({}, _.pick(currentQuery, SORT_KEY), query))
    }
  }
}

export const PrimarySearchViewEnum = new Enum(PrimarySearchView)

export const getCurrentPrimaryView = (query, currentUserId) => {
  const activeView = _.chain(PrimarySearchView)
    .map((value, key) => ({ value, key }))
    .filter(({ value }) => value.isActive(query, currentUserId))
    .map(({ key }) => key)
    .first()
    .value()

  return activeView || ADVANCED_SEARCH_KEY
}

export const SecondarySearchView = {
  open: {
    name: 'Open',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const secondaryFilters = filters[STATUS_FILTER_KEY]

      return _.isEqual(secondaryFilters, [ConversationStatusEnum.OPEN.key])
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[STATUS_FILTER_KEY] = [ConversationStatusEnum.OPEN.key]
      return makeQuery(nextQuery)
    }
  },
  pending: {
    name: 'Pending',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const secondaryFilters = filters[STATUS_FILTER_KEY]

      return _.isEqual(secondaryFilters, [ConversationStatusEnum.PENDING.key])
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[STATUS_FILTER_KEY] = [ConversationStatusEnum.PENDING.key]
      return makeQuery(nextQuery)
    }
  },
  closed: {
    name: 'Closed',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const secondaryFilters = filters[STATUS_FILTER_KEY]

      return _.isEqual(secondaryFilters, [ConversationStatusEnum.CLOSED.key])
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[STATUS_FILTER_KEY] = [ConversationStatusEnum.CLOSED.key]
      return makeQuery(nextQuery)
    }
  },
  all: {
    name: 'All',
    isActive(query) {
      const filters = _.pick(query, FILTER_KEYS)
      const secondaryFilters = filters[STATUS_FILTER_KEY]

      return _.isEmpty(secondaryFilters)
        || _.isEqual(new Set(secondaryFilters), new Set([
          ConversationStatusEnum.OPEN.key,
          ConversationStatusEnum.PENDING.key,
          ConversationStatusEnum.CLOSED.key,
        ]))
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[STATUS_FILTER_KEY] = undefined
      return makeQuery(nextQuery)
    }
  }
}

export const SecondarySearchViewEnum = new Enum(SecondarySearchView)

export const getCurrentSecondaryView = (query) => {
  const activeView = _.chain(SecondarySearchView)
    .map((value, key) => ({ value, key }))
    .filter(({ value }) => value.isActive(query))
    .map(({ key }) => key)
    .first()
    .value()

  return activeView
}

export const SortView = {
  newest: {
    name: 'Newest',
    isActive(query) {
      const sortKeys = _.pick(query, SORT_KEYS)
      const sortKey = sortKeys[SORT_KEY]
      return _.isEmpty(sortKey) || _.isEqual(sortKey, SortConversationsEnum.NEWEST.value)
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[SORT_KEY] = undefined
      return makeQuery(nextQuery)
    }
  },
  oldest: {
    name: 'Oldest',
    isActive(query) {
      const sortKeys = _.pick(query, SORT_KEYS)
      const sortKey = sortKeys[SORT_KEY]
      return _.isEqual(sortKey, SortConversationsEnum.OLDEST.value)
    },
    getQuery(currentQuery) {
      const nextQuery = Object.assign({}, currentQuery)
      nextQuery[SORT_KEY] = SortConversationsEnum.OLDEST.value
      return makeQuery(nextQuery)
    }
  },
}

export const SortViewEnum = new Enum(SortView)

export const sortViewFromQuery = (query) => {
  const activeView = _.chain(SortView)
    .map((value, key) => ({ value, key }))
    .filter(({ value }) => value.isActive(query))
    .map(({ key }) => key)
    .first()
    .value()

  return activeView
}



// WEBPACK FOOTER //
// ./src/scripts/constants/SearchViews.js